// Thin createServerFn wrappers for the trauma player. Logic lives in attempts.server.ts.
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

async function deps(userId: string) {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { isTraumaEnabled } = await import("@/lib/trauma/gate.server");
  const core = await import("@/lib/trauma/attempts.server");
  return { core, d: { admin: supabaseAdmin, userId, isEnabled: isTraumaEnabled } };
}

const obj = (v: unknown) => (v && typeof v === "object" ? (v as Record<string, unknown>) : {});

export const getTraumaPlayerState = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((v: { courseId: string }) => ({ courseId: obj(v).courseId }))
  .handler(async ({ data, context }) => {
    const { core, d } = await deps(context.userId);
    return core.getPlayerStateCore(d, data);
  });

export const saveTraumaAnswer = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((v: { attemptId: string; questionN: number; pos: number }) => {
    const o = obj(v);
    return { attemptId: o.attemptId, questionN: o.questionN, pos: o.pos };
  })
  .handler(async ({ data, context }) => {
    const { core, d } = await deps(context.userId);
    return core.saveAnswerCore(d, data);
  });

export const completeTraumaAttempt = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((v: { attemptId: string }) => ({ attemptId: obj(v).attemptId }))
  .handler(async ({ data, context }) => {
    const { core, d } = await deps(context.userId);
    return core.completeAttemptCore(d, data);
  });

export const getTraumaResult = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((v: { attemptId: string }) => ({ attemptId: obj(v).attemptId }))
  .handler(async ({ data, context }) => {
    const { core, d } = await deps(context.userId);
    return core.getResultCore(d, data);
  });

export const startTraumaRetake = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((v: { courseId: string }) => ({ courseId: obj(v).courseId }))
  .handler(async ({ data, context }) => {
    const { core, d } = await deps(context.userId);
    return core.startRetakeCore(d, data);
  });

export const markTraumaCta = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((v: { attemptId: string; kind: "book" | "next" | "share" }) => {
    const o = obj(v);
    return { attemptId: o.attemptId, kind: o.kind };
  })
  .handler(async ({ data, context }) => {
    const { core, d } = await deps(context.userId);
    return core.markCtaCore(d, data);
  });

export const listMyTraumaCourses = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { core, d } = await deps(context.userId);
    return core.listMyCoursesCore(d);
  });

export const getTraumaLessons = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((v: { courseId: string }) => ({ courseId: obj(v).courseId }))
  .handler(async ({ data, context }) => {
    const { core, d } = await deps(context.userId);
    return core.getLessonsCore(d, data);
  });
